/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { SVGProps } from 'react';

/**
 * Live BG's shopping-bag glyph, transcribed from live's own asset.
 *
 * WHAT THIS REPLACES, AND WHY MEASURING THE BOX REPORTED PARITY
 *
 * BG was wearing NM's bag — `NmCartIcon` — squeezed into a box fitted to BG's
 * scanned ink. The box was right and the drawing was wrong, so every box-level
 * check agreed while the mark on screen belonged to the other brand.
 *
 *     glyph                     box            painted ink       aspect
 *     NmCartIcon    (before)    22 x 25.5      22.00 x 25.00     0.880
 *     this asset    (after)     30 x 30        21.60 x 25.53     0.846
 *     live BG                   30 x 30        21.5  x 25.5      0.843
 *
 * The two marks are different drawings, not one drawing at two scales. NM's is
 * a SEMICIRCULAR handle over a square-cornered body; BG's is a squared-off
 * handle with rounded shoulders over a rounded-corner body, and it carries a
 * detail NM's has nowhere: the RIGHT handle leg continues 4.4 units down INTO
 * the bag interior as a round-ended stub, with no mirror on the left. That
 * asymmetric stub is the fastest way to tell the two marks apart at a glance
 * and it is invisible in any bounding box.
 *
 * PROVENANCE — live's own file, not a measurement of it
 *
 * The `<path>` below is byte-identical to the `d` of the inline
 * `data:image/svg+xml;base64` asset on the `alt="Shopping Bag containing N
 * items"` img, decoded from four independent BG dumps in the evidence tree:
 * `.reference/bg-homepage.mobile.html`, `.reference/bg-homepage.desktop.html`,
 * `.reference/bg-search-dress.desktop.html` and
 * `.reference/bg-plp-womens-dresses.desktop.html`. All four carry the same 660
 * base64 characters. Provenance was checked INSIDE each file rather than from
 * its name: all four declare a `<title>` naming Bergdorf Goodman.
 *
 * THE ALT IS THE HANDLE, AND IT IS NOT A CLASS. This img carries no class at
 * all, so the class-name handle that finds both brands' hamburgers finds
 * nothing here; the alt text is the only stable way in, and it differs per
 * brand. NM's dumps contain no `Shopping Bag containing` string whatsoever.
 *
 * Live declares `width="30" height="30" viewBox="0 0 30 30"`, which is why the
 * viewBox here is 30 and why the box in lib/brand/header-composition.ts moves
 * to live's own 30px — at which this art renders at scale 1 and its ink lands
 * on live's scanned ink to within a tenth of a pixel.
 *
 * The capture tree could not answer this: the decompiler REHOSTS images, and
 * `.reference/captures/bg-home-m-375/App.tsx:105` is a storage.googleapis.com
 * URL with no width or height class. The raw dumps preserve live's own markup
 * and were the only source that had the vector.
 *
 * GEOMETRY, read off the path — one filled compound path, three subpaths,
 * even-odd winding, no stroke. Wall thickness is a constant 1.600 throughout:
 *
 *     part                 x                  y                  thickness
 *     body outer           4.000 -> 25.600    9.808 -> 27.408    1.600
 *     body inner (hole)    5.600 -> 24.000    11.408 -> 24.608   -
 *     handle outer        10.703 -> 18.898    1.881 ->  9.808    1.600
 *     handle inner        12.303 -> 17.298    3.481 ->  9.808    -
 *     interior stub       17.298 -> 18.898    11.408 -> 15.816   1.600
 *
 * Corner radii: 0.8 at the bag's top corners, 2.8 at its bottom corners,
 * 3.841 outer / 2.241 inner on the handle shoulders, and a 0.8 semicircular
 * cap on the stub. Ink box 21.600 x 25.527, aspect 0.8462 — a PORTRAIT
 * silhouette, against the 0.911 landscape-leaning NmCartIcon it replaces.
 *
 * The mark sits 0.2 left of the box's horizontal centre (insets 4.000 left,
 * 4.400 right) and 0.355 above its vertical centre (1.881 top, 2.592 bottom).
 * Both offsets are live's and are reproduced rather than corrected, because
 * the box is what positions the mark.
 *
 * TWO SOURCES AGREE. A colour-managed pixel scan of live's bag, taken by an
 * earlier agent off `.reference/live-shots/bg-search-390.png` at DPR 2 and
 * recorded in lib/brand/header-composition.ts beside `cartGlyphBox`, read
 * 21.5 x 25.5. This asset's own numbers land 0.100 and 0.027 away from that —
 * inside a fifth of a device pixel, from a completely independent instrument.
 * Neither reading was derived from the other.
 *
 * DELIBERATE DIVERGENCE FROM THE SOURCE: `fill="currentColor"`, where live's
 * path declares no fill and therefore paints black. Ours inherits the header
 * foreground, as every other glyph in this directory does. `fill-rule` is
 * live's own `evenodd` and is load-bearing here rather than decorative — the
 * body hole and the handle hole are subpaths of the same path, and under
 * nonzero winding they would fill solid.
 *
 * WHY THE FILE IS NOT NAMED `bg-cart-icon`. That name would form an
 * `nm-`/`bg-` same-stem pair with `nm-cart-icon.tsx`, which the structural
 * scan in src/__tests__/brand-fork.test.ts reads as one concept implemented
 * twice. These are not two implementations — they are two DRAWINGS, selected
 * by `CART_GLYPH` in components/header/cart-badge-icon.tsx, which is exactly
 * the brand-keyed map that gate exists to push work towards. `bag` is BG's own
 * word for the control — its alt text says Shopping Bag and its desktop
 * utility link is literally "Bag" — so the name is live BG's rather than an
 * evasion, but the choice is worth stating plainly: the alternative is a
 * paired name plus a recorded reason in that gate's baseline, which would grow
 * a ratchet for something that is not a fork.
 */
export function BgBagIcon(props: SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.703 9.808V5.713a3.841 3.841 0 013.833-3.832h.529a3.841 3.841 0 013.833 3.832v4.095H24.8a.8.8 0 01.8.8v14c0 1.542-1.258 2.8-2.8 2.8h-16a2.806 2.806 0 01-2.8-2.8v-14a.8.8 0 01.8-.8h5.903zm8.195 1.6v3.608a.8.8 0 11-1.6 0v-3.608H5.6v13.2c0 .658.542 1.2 1.2 1.2h16c.658 0 1.2-.542 1.2-1.2v-13.2h-5.102zm-1.6-1.6V5.713a2.241 2.241 0 00-2.233-2.232h-.53a2.241 2.241 0 00-2.232 2.232v4.095h4.995z"
                fill="currentColor"
            />
        </svg>
    );
}
